/**
 * Audio readiness probe.
 *
 * Answers one question, once, at app start: can this device make a sound
 * *before* anyone touches the screen?
 *
 * That has to be measured at start-up, because the first touch unlocks audio
 * and destroys the evidence. Navigating to a diagnostics screen is itself a
 * touch — so by the time you could look, the answer would always be "fine".
 *
 * Two separate browser regimes are checked, because they can differ:
 *   - Web Audio API (AudioContext)  → the synthesized ringtone
 *   - HTMLAudioElement (<audio>)    → the mp3 ringtone
 * If HTML audio is allowed while Web Audio is blocked, replacing the
 * synthesized tone with a sound file fixes ringing with no MDM or app change.
 *
 * Runs silently: the probe clip is 44 bytes of digital silence at volume 0.
 */

export interface AudioReadiness {
  /** AudioContext state before any resume attempt: 'running' means unlocked. */
  contextState: string;
  /** Did resume() settle? On a blocked context it stays PENDING, not rejected. */
  resume: 'resolved' | 'pending' | 'error';
  /** Can Web Audio actually produce sound now? */
  webAudio: boolean;
  /** Can an <audio> element play now? This is the interesting one. */
  htmlAudio: boolean;
  /** Has this document ever seen a user gesture? false = a genuine cold check. */
  hasBeenActive: boolean | null;
  /** When the probe ran. */
  at: string;
}

const SILENT_WAV =
  'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

let result: AudioReadiness | null = null;
let running: Promise<AudioReadiness> | null = null;

/** The probe result, or null if it has not finished yet. */
export function getAudioReadiness(): AudioReadiness | null {
  return result;
}

export function measureAudioReadiness(): Promise<AudioReadiness> {
  if (running) return running;

  running = (async (): Promise<AudioReadiness> => {
    const readiness: AudioReadiness = {
      contextState: 'unknown',
      resume: 'error',
      webAudio: false,
      htmlAudio: false,
      hasBeenActive:
        typeof navigator !== 'undefined' && 'userActivation' in navigator
          ? (navigator as Navigator & { userActivation: { hasBeenActive: boolean } })
              .userActivation.hasBeenActive
          : null,
      at: new Date().toISOString(),
    };

    // ── Web Audio ────────────────────────────────────────────────
    try {
      const ctx = new AudioContext();
      readiness.contextState = ctx.state;
      // resume() on a blocked context never settles, so race it against a
      // timeout instead of awaiting it directly.
      readiness.resume = await Promise.race([
        ctx.resume().then(() => 'resolved' as const).catch(() => 'error' as const),
        new Promise<'pending'>((r) => setTimeout(() => r('pending'), 1500)),
      ]);
      readiness.webAudio = ctx.state === 'running';
      // Close only if we opened it purely for the probe and it is unusable;
      // a running context is left alone so CallTones can keep using it.
      if (!readiness.webAudio) {
        try { await ctx.close(); } catch { /* already closed */ }
      }
    } catch {
      readiness.contextState = 'unavailable';
    }

    // ── HTMLAudioElement ─────────────────────────────────────────
    try {
      const el = new Audio(SILENT_WAV);
      el.volume = 0;
      await el.play();
      readiness.htmlAudio = true;
      el.pause();
    } catch {
      readiness.htmlAudio = false;
    }

    result = readiness;
    return readiness;
  })();

  return running;
}

/** One-line summary for logs and the info screen. */
export function formatAudioReadiness(r: AudioReadiness | null): string {
  if (!r) return 'meting loopt nog…';
  if (r.webAudio && r.htmlAudio) return 'OK — beide geluidsbronnen toegestaan';
  if (!r.webAudio && r.htmlAudio) return 'Web Audio geblokkeerd, mp3 toegestaan';
  if (r.webAudio && !r.htmlAudio) return 'Web Audio toegestaan, mp3 geblokkeerd';
  return 'GEBLOKKEERD — geen geluid mogelijk zonder aanraking';
}
